import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import CardTitle from '@auteco/components/sharedComponents/CardTitle';
import MaterialTable from '@material-table/core';
import { Card, TextField, Typography } from '@material-ui/core';
import { Colors } from '@auteco/theme';
import { Alert } from '@auteco/components/snopComponents/Alert';
import { Autocomplete } from '@material-ui/lab';
import { verifyNullValue } from '@auteco/utils/verifyNullValue';
import { postGroupMaterialExport } from '@auteco/services/snopServices';
import { TABLE_CONFIG } from './constants';
import { useExcelExport } from './hooks/useExcelExport';
import { useMaterialManagement } from './hooks/useMaterialManagement';
import { useTableColumns } from './hooks/useTableColumns';
import TableToolbar from './components/TableToolbar';
import useStyles from './styles';

const inverseLogisticP = (y) => {
  if (!y) return 0;
  y /= 100;
  if (y < 0 || y > 1) {
    throw new Error('El valor de y debe estar en el rango (0, 1)');
  }
  const result = -Math.log((1 - y) / (y + 1));
  return result * 100;
};

const PendingMaterialsTable = ({
  profiles,
  groups,
  materials,
  ids,
  getGroups,
  getMaterials,
  handleViewDetails,
  handleGetDataByGroupId,
  setMaterials,
}) => {
  const classes = useStyles();
  const checkboxRef = useRef({ current: [] });
  const tableRef = useRef();
  const LOCAL_STORAGE_KEY = 'pendingMaterialsTableColumns';

  const { exportToExcel } = useExcelExport();

  const {
    alertInfo,
    idSaved,
    setIdSaved,
    onCloseAlert,
    onPassMaterial,
    handleFinishMaterial,
    onAssignMaterials,
  } = useMaterialManagement(materials, setMaterials, getMaterials);

  const baseColumns = useTableColumns(
    materials,
    handleViewDetails,
    onPassMaterial,
    handleFinishMaterial,
    inverseLogisticP
  );

  // Estado para las columnas configuradas
  const [columns, setColumns] = useState(baseColumns);

  // Guardar configuración de columnas
  const saveColumnSettings = () => {
    try {
      if (tableRef.current) {
        const currentColumns = tableRef.current.state.columns;
        if (currentColumns) {
          // Guardar solo los campos necesarios
          const columnsConfig = currentColumns.map(col => ({
            field: col.field,
            hidden: col.hidden
          }));
          localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(columnsConfig));
        }
      }
    } catch (error) {
      console.error('Error al guardar configuración de columnas:', error);
    }
  };

  // Cargar configuración de columnas al iniciar
  useEffect(() => {
    try {
      const savedColumnsConfig = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (savedColumnsConfig) {
        const savedColumns = JSON.parse(savedColumnsConfig);
        
        // Aplicar configuración guardada a las columnas base
        const updatedColumns = baseColumns.map(col => {
          const savedCol = savedColumns.find(c => c.field === col.field);
          if (savedCol) {
            return { ...col, hidden: savedCol.hidden };
          }
          return col;
        });
        
        setColumns(updatedColumns);
      } else {
        setColumns(baseColumns);
      }
    } catch (error) {
      console.error('Error al cargar configuración de columnas:', error);
      setColumns(baseColumns);
    }
  }, [baseColumns]);

  // Actualizar columnas cuando cambian los materiales
  useEffect(() => {
    if (tableRef.current) {
      // Guardar la configuración actual
      saveColumnSettings();
    }
  }, [materials]);

  // Manejar cambios en la visibilidad de columnas
  const handleColumnHiddenChange = () => {
    setTimeout(saveColumnSettings, 100);
  };

  // Manejar arrastre de columnas
  const handleColumnDragged = () => {
    setTimeout(saveColumnSettings, 100);
  };

  const getGroupsProfile = ({ id }) => {
    getGroups({ id });
  };

  const getMaterialsProfile = ({ id }) => {
    getMaterials({ id });
    handleGetDataByGroupId({ id });
    setIdSaved(id);
  };

  const refreshData = async () => {
    getMaterials({ id: idSaved });
    handleGetDataByGroupId({ id: idSaved });
  };

  const exportMaterial = async (id) => {
    const data = await postGroupMaterialExport(id);
    exportToExcel(data);
  };

  return (
    <>
      <Alert
        title={alertInfo.title}
        content={alertInfo.content}
        textBtn1={alertInfo.textBtn1}
        textBtn2={alertInfo.textBtn2}
        funcBtn={alertInfo.funcBtn}
        open={alertInfo.open}
        onClose={onCloseAlert}
      />
      <CardTitle title="Listado de materiales pendientes" />
      <Card style={{ padding: '12px' }}>
        <Typography>
          Selecciona un perfil y después un grupo para ver sus materiales
        </Typography>
        <Autocomplete
          options={profiles}
          value={profiles.find((option) => option.id === ids.profile) || null}
          className={classes.textField}
          getOptionLabel={(option) => option.nombre}
          renderInput={(params) => (
            <TextField {...params} label="Perfiles" variant="outlined" />
          )}
          onChange={(e, newValue) =>
            getGroupsProfile({
              id: verifyNullValue({ value: newValue, param: 'id' }),
            })
          }
        />
        <Autocomplete
          options={groups}
          disabled={groups?.length === 0}
          value={groups.find((option) => option.id === ids.group) || null}
          className={classes.textField}
          getOptionLabel={(option) => option.nombre}
          renderInput={(params) => (
            <TextField {...params} label="Grupos" variant="outlined" />
          )}
          onChange={(e, newValue) =>
            getMaterialsProfile({
              id: verifyNullValue({ value: newValue, param: 'id' }),
            })
          }
        />
        {materials.length > 0 && (
          <>
            <TableToolbar
              onAssignMaterials={() => onAssignMaterials(checkboxRef.current)}
              onExportMaterial={exportMaterial}
              idSaved={idSaved}
              refreshData={refreshData}
            />
            <MaterialTable
              tableRef={tableRef}
              title=""
              data={materials}
              columns={columns}
              style={{ marginTop: '20px' }}
              onSelectionChange={(items) => {
                if (items && Array.isArray(items)) {
                  checkboxRef.current = items;
                }
              }}
              onColumnDragged={handleColumnDragged}
              onChangeColumnHidden={handleColumnHiddenChange}
              options={{
                selection: true,
                sorting: false,
                thirdSortClick: false,
                rowStyle: (rowData, index) => ({
                  backgroundColor:
                    index % 2 === 0 ? 'white' : Colors.GRAY_BACKGROUND,
                  padding: '5px',
                  fontSize: '12px',
                }),
                pageSize: TABLE_CONFIG.DEFAULT_PAGE_SIZE,
                columnsButton: true,
                pageSizeOptions: TABLE_CONFIG.PAGE_SIZES,
                headerStyle: {
                  backgroundColor: Colors.BLUE,
                  color: '#98e2f0',
                  fontSize: '14px',
                  fontWeight: 700,
                  position: 'sticky',
                  zIndex: '10',
                },
                maxBodyHeight: TABLE_CONFIG.MAX_BODY_HEIGHT,
                selectionProps: (rowData) => ({
                  color:
                    rowData?.tableData?.estado === 1 ? 'primary' : 'secondary',
                }),
                actionsColumnIndex: -1,
              }}
            />
          </>
        )}
      </Card>
    </>
  );
};

PendingMaterialsTable.propTypes = {
  profiles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      nombre: PropTypes.string.isRequired,
    })
  ).isRequired,
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      nombre: PropTypes.string.isRequired,
    })
  ).isRequired,
  materials: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      // Otros campos pueden ser opcionales
    })
  ).isRequired,
  ids: PropTypes.shape({
    profile: PropTypes.string,
    group: PropTypes.string,
  }).isRequired,
  getGroups: PropTypes.func.isRequired,
  getMaterials: PropTypes.func.isRequired,
  handleViewDetails: PropTypes.func.isRequired,
  handleGetDataByGroupId: PropTypes.func.isRequired,
  setMaterials: PropTypes.func.isRequired,
};

export { PendingMaterialsTable };
